import React, {useEffect, useState} from 'react';
import {Button, Heading} from '@taopix/taopix-design-system';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {CalendarSelector, LayoutSelector} from '../../../../Selectors';
import {useSelector} from 'react-redux';
import {CalendarOptions} from '@taopix/taopix-core/dist/types/types';
import {AssistantHeader} from '../../AssistantHeader';
import {AssistantFooter} from '../../AssistantFooter';
import {AssistantContent} from '../../AssistantContent';
import {AssistantNav} from '../../AssistantNav';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import {useTranslation} from 'react-i18next';
import {resultType} from '../../../Palette/Calendars/CalendarPalette';
import CalendarSettings from '../../../Palette/Calendars/CalendarSettings';
import {useUserFlowState} from '../../../Context/UserFlow/UserFlowContext';
import {
  AssistantAssetsProps,
  AssistantCommonProps,
  AssistantNavigationProps,
} from '../../Assistant';
import {useExperience} from '../../../Experience/ExperienceProvider';

type ManageAssetsStepProps = {} & AssistantCommonProps &
  AssistantAssetsProps &
  AssistantNavigationProps;

export const CalendarSettingsStep = ({
  assets,
  onClose,
  backButton,
  mediaGalleryProps,
  ...props
}: ManageAssetsStepProps) => {
  const ui = useUiState();
  const {t} = useTranslation(['*', 'uploadexternalassets']);
  const userFlow = useUserFlowState();
  const calendarManager = window?.Taopix?.Core?.get('calendarManager');
  const calendarOptions = calendarManager?.getOptionMenus();

  // get calendar settings from store
  const calendarStoreSettings = useSelector(CalendarSelector).settings;
  const productLayout = useSelector(LayoutSelector);

  const {experience, updateExperience} = useExperience();

  const [calendarOptionSettings, setCalendarOptionSettings] = useState<Partial<CalendarOptions>>({
    startMonth: calendarStoreSettings.startMonth,
    startYear: calendarStoreSettings.startYear,
    startDay: calendarStoreSettings.startDay,
    numberOfMonths: calendarStoreSettings.numberOfMonths,
    locale: calendarStoreSettings.locale,
  });

  const listItemData = (() => {
    const initialMonths = calendarOptions?.months;
    const initialDays = calendarOptions?.days;
    const locales = calendarOptions?.locales;
    const availableYears = calendarOptions?.years;

    const result: resultType = {
      months: [],
      days: [],
      years: [],
      locales: [],
    };

    for (const key in initialMonths) {
      result.months.push({label: initialMonths[key].month, value: key});
    }

    result.days.push({label: t('*:str_LabelFirstDayOfTheMonth'), value: '0'});
    for (const key in initialDays) {
      result.days.push({label: initialDays[key].day, value: key});
    }

    for (const value in locales) {
      result.locales.push({label: t(`calendars:${locales[value]}`), value: value});
    }

    availableYears?.forEach(y => {
      result.years.push({label: y.toString(), value: y.toString()});
    });

    return result;
  })();

  const noChangedSettings = (settings: Partial<CalendarOptions>): boolean => {
    const commonKeys = Object.keys(settings).filter(key =>
      calendarStoreSettings.hasOwnProperty(key)
    );
    return commonKeys.every(key => calendarStoreSettings[key] === settings[key]);
  };

  return (
    <>
      <AssistantHeader
        displayMode={props.displayMode}
        pageCount={productLayout.pagecount}
      ></AssistantHeader>
      <AssistantNav
        onClose={function (): void {
          throw new Error('Function not implemented.');
        }}
        backButton={<></>}
        nextButton={
          <Button
            label={t('*:str_TitleNext')}
            className={'ml-auto'}
            onClick={() => {
              // If the showAddPicturesStep if false we want to go straight to the editor.
              const nextFn = experience.WIZARD.addPicturesStep.showAddPicturesStep
                ? userFlow.setNextStep
                : props.nextButton.props.onClick;
              if (noChangedSettings(calendarOptionSettings)) {
                nextFn();
                return;
              }

              ui.dispatch(UserInterfaceActions.setIsBusy(true));
              calendarManager
                .updateSettings(calendarOptionSettings)
                .then(() => {
                  nextFn();
                  ui.dispatch(UserInterfaceActions.setIsBusy(false));
                })
                .catch(error => {
                  ui.dispatch(UserInterfaceActions.setIsBusy(false));
                });
            }}
          />
        }
        onNext={() => {}}
      />
      <AssistantContent>
        <div className="flex flex-col items-center h-screen my-md">
          <div className={'space-y-sm w-[min(theme(width.bounds),400rem)]'}>
            <Heading>{t('*:str_TitleCalendarOptions')}</Heading>
            <CalendarSettings
              listItemData={listItemData}
              onChangeSettings={(data: Partial<CalendarOptions>) => {
                setCalendarOptionSettings({
                  ...calendarOptionSettings,
                  ...data,
                });
              }}
              showApplyButtonInPanel={false}
              calendarOptions={calendarOptions}
              calendarSettings={calendarStoreSettings}
              canChangeNumberOfMonths={
                calendarOptions?.numberOfMonths?.min !== calendarOptions?.numberOfMonths?.max
              }
            />
          </div>
        </div>
      </AssistantContent>
      <AssistantFooter />
      <div className="catch-zone"></div>
    </>
  );
};
